import { parseAsStringLiteral, useQueryState } from "@msuite/ui/libs/nuqs";
import { useQuery } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { GetOrdersQuerySchema } from "@msuite/utils/schemas";
import { ORDER_STATES } from "@msuite/utils/stock";
import { client } from "@/hono";
import { SubordersTable } from "../suborders-table";
import { OrdersMenu } from "./orders-menu";
import { columns } from "./table-config";

/** Props Interface */
interface OrdersTableProps {
	query?: GetOrdersQuerySchema;
	persistenceKey?: `${string}-table`;
}

export function OrdersTable({ query, persistenceKey = "orders-table" }: OrdersTableProps) {
	/**
	 * State
	 *
	 * The filter lives in the URL rather than under `persistenceKey`: that key persists the
	 * table's own layout — column order, widths, pinning — and a filter that narrows what the
	 * server returns is not layout. In the URL it is a link somebody can send, and it is gone on
	 * the next visit rather than quietly narrowing it.
	 *
	 * It opens on `pending`, the state a freshly raised order holds, so the list starts on the
	 * work that has just come in. Finished and cancelled orders sit behind their own segments,
	 * and `all` is the one that shows every state at once.
	 */
	const [state, setState] = useQueryState(
		"order-state",
		parseAsStringLiteral(["all", ...ORDER_STATES] as const).withDefault("pending"),
	);

	/** Computed */
	const fullQuery: GetOrdersQuerySchema = {
		...query,
		state: state === "all" ? undefined : [state],
	};

	/** Query */
	const { data, isLoading, refetch, queryKey } = useQuery({
		queryKey: ["orders", JSON.stringify(fullQuery)],
		queryFn: async () => {
			const response = await client.stock.orders.$get({ query: fullQuery });
			const data = await responseHandler(response);
			return data.orders;
		},
	});

	/** Render */
	return (
		<DataTable
			data={data}
			columns={columns}
			queryKey={queryKey}
			isLoading={isLoading}
			refetch={refetch}
			persistenceKey={persistenceKey}
			// The state is what the list is read for, and it sits behind six columns — so it
			// starts pinned beside the row actions. This is a default and not a rule: the persisted
			// layout under `persistenceKey` wins, which is why the two standalone lists carry keys
			// of their own rather than sharing the one the project panel already wrote.
			initialPinningState={{ end: ["state"] }}
			menu={
				<OrdersMenu
					query={query}
					state={state}
					onStateChange={setState}
				/>
			}
			getRowCanExpand={() => true}
			renderSubRows={(row) => (
				<SubordersTable
					type="sub-table"
					orderId={row.original.id}
					refetch={refetch}
				/>
			)}
		/>
	);
}
